import React, { useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import {
  useThemeColors,
  useThemedStyles,
  type ThemeContextValue,
} from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import { RNButton } from '@/components';
import { getIsOverdue, getInCredit } from '@/features/payments/common/helpers';
import { ChargeBreakdown } from '../shared/ChargeBreakdown';
import { AccountCreditIndicator } from '../shared/AccountCreditIndicator';
import type { BalanceItem } from '@/types/residents/lease';

interface MobilePaymentCardProps {
  leaseId: number;
  residentName?: string;
  propertyName?: string;
  unitNumber?: string;
  balanceDue: number;
  accountCredit?: number;
  autopayEnabled?: boolean;
  isConnection?: boolean;
  paymentsBlocked?: boolean;
  balanceItems?: BalanceItem[];
  onPress?: () => void;
}

export const MobilePaymentCard: React.FC<MobilePaymentCardProps> = ({
  leaseId,
  residentName = 'Resident',
  propertyName = '',
  unitNumber = '',
  balanceDue,
  accountCredit = 0,
  autopayEnabled = false,
  isConnection = false,
  paymentsBlocked = false,
  balanceItems,
  onPress,
}) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  const isOverdue = useMemo(() => getIsOverdue(balanceDue), [balanceDue]);
  const inCredit = useMemo(() => getInCredit(balanceDue), [balanceDue]);
  const shouldPay = useMemo(() => {
    return ((balanceDue || 0) > 0 && !inCredit) || isOverdue;
  }, [balanceDue, inCredit, isOverdue]);

  const handleCardPress = () => {
    if (onPress) {
      onPress();
    } else {
      navigation.navigate('PaymentDetail', { leaseId });
    }
  };

  const handleMakePayment = (e: any) => {
    e.stopPropagation();
    navigation.navigate('MakePayment', { leaseId });
  };

  return (
    <TouchableOpacity
      style={[
        styles.card,
        isOverdue && styles.cardOverdue,
      ]}
      onPress={handleCardPress}
      activeOpacity={0.7}
    >
      {/* Overdue Banner */}
      {isOverdue && (
        <View style={styles.overdueBanner}>
          <Text style={styles.overdueText}>Past Due</Text>
        </View>
      )}

      <View style={styles.cardContent}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.residentName} numberOfLines={1}>
              {residentName}
            </Text>
            <View style={styles.propertyRow}>
              <Text style={styles.propertyText} numberOfLines={1}>
                {propertyName}
                {unitNumber ? ` • ${unitNumber}` : ''}
              </Text>
            </View>
          </View>

          {autopayEnabled && (
            <View style={styles.autopayBadge}>
              <CheckIcon color={semantic.status.success} />
              <Text style={styles.autopayText}>Autopay</Text>
            </View>
          )}
        </View>

        {/* Balance */}
        <View style={styles.balanceSection}>
          <Text style={[
            styles.balance,
            isOverdue && styles.balanceOverdue,
            inCredit && styles.balanceCredit,
          ]}>
            {formatCurrency(balanceDue)}
          </Text>
          <Text style={styles.balanceLabel}>
            {shouldPay ? 'Balance due' : inCredit ? 'Credit' : 'No balance due'}
          </Text>
        </View>

        <AccountCreditIndicator amount={accountCredit} variant="inline" />

        {/* Charge Details */}
        {balanceItems && balanceItems.length > 0 && !paymentsBlocked && (
          balanceItems.length > 5 ? (
            <ScrollView
              style={styles.chargeDetailsScroll}
              nestedScrollEnabled
              showsVerticalScrollIndicator
            >
              <ChargeBreakdown
                balanceItems={balanceItems}
                balanceDue={balanceDue}
                accountCredit={accountCredit}
                variant="mobileLight"
              />
            </ScrollView>
          ) : (
            <ChargeBreakdown
              balanceItems={balanceItems}
              balanceDue={balanceDue}
              accountCredit={accountCredit}
              variant="mobileLight"
              style={styles.chargeDetails}
            />
          )
        )}

        {/* Action Button */}
        {!paymentsBlocked && shouldPay && (
          <RNButton
            title="Make Payment"
            onPress={handleMakePayment}
            variant="tertiary"
            style={StyleSheet.flatten([
              styles.paymentButton,
              isOverdue && styles.paymentButtonOverdue,
            ])}
            textStyle={styles.paymentButtonText}
          />
        )}

        {paymentsBlocked && (
          <Text style={styles.blockedText}>
            Payments are currently unavailable
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

const CheckIcon: React.FC<{ color: string }> = ({ color }) => {
  const Icon = RESIDENTS_SVG_COMPONENTS.CHECK as any;
  if (!Icon) return null;
  return (
    <View style={{ marginRight: 4 }}>
      <Icon width={12} height={12} color={color} />
    </View>
  );
};

const makeStyles = ({ semantic, components }: ThemeContextValue) =>
  StyleSheet.create({
    card: {
      // Was PAYMENT_COLORS.WHITE — a fixed white card (bright island in dark).
      // Matches MobilePaymentCardLightSkeleton's components.card.background.
      backgroundColor: components.card.background,
      borderRadius: 12,
      marginHorizontal: 16,
      marginVertical: 6,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.06,
      shadowRadius: 2,
      elevation: 2,
      overflow: 'hidden',
    },
    cardOverdue: {
      borderColor: semantic.status.error,
      borderWidth: 2,
    },
    overdueBanner: {
      backgroundColor: semantic.status.error,
      paddingHorizontal: 12,
      paddingVertical: 6,
      alignItems: 'center',
    },
    overdueText: {
      // White on the solid red danger banner (standard danger pattern).
      color: '#FFFFFF',
      fontSize: 12,
      fontWeight: '600',
    },
    cardContent: {
      padding: 16,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 12,
    },
    headerLeft: {
      flex: 1,
      marginRight: 12,
    },
    residentName: {
      fontSize: 17,
      fontWeight: '600',
      color: semantic.text.primary,
      marginBottom: 4,
    },
    propertyRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    propertyText: {
      fontSize: 14,
      color: semantic.text.tertiary,
    },
    autopayBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: semantic.status.successBg,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
    },
    autopayText: {
      fontSize: 11,
      color: semantic.status.success,
      fontWeight: '600',
    },
    balanceSection: {
      marginBottom: 12,
    },
    balance: {
      fontSize: 28,
      fontWeight: '700',
      color: semantic.text.primary,
      marginBottom: 2,
    },
    balanceOverdue: {
      color: semantic.status.error,
    },
    balanceCredit: {
      color: semantic.status.success,
    },
    balanceLabel: {
      fontSize: 13,
      color: semantic.text.tertiary,
    },
    chargeDetails: {
      marginBottom: 12,
    },
    chargeDetailsScroll: {
      maxHeight: 200,
      marginBottom: 12,
    },
    paymentButton: {
      backgroundColor: semantic.interactive.primary,
      paddingVertical: 12,
    },
    paymentButtonOverdue: {
      backgroundColor: semantic.status.error,
    },
    paymentButtonText: {
      // On-primary ink — readable on the solid blue / red button fill (was a
      // gray secondary text that read poorly on the fill).
      color: components.button.primaryText,
      fontWeight: '600',
    },
    blockedText: {
      fontSize: 13,
      color: semantic.text.tertiary,
      textAlign: 'center',
      fontStyle: 'italic',
    },
  });
